import React, { useCallback, useEffect, useState } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"

import Layout from "components/Layout"
import Avatar from "react-avatar"

import UserModal from "components/Common/Forms/User"

import useInfiniteScroll from "hooks/useInfiniteScroll"

import { toast } from "react-hot-toast"

import { uri } from "config"

const USERS = gql`
  query Users(
    $filter: UserFilterInput
    $page: Int
    $size: Int
    $sort: UserSortInput
  ) {
    users(filter: $filter, page: $page, size: $size, sort: $sort) {
      id
      displayName
      email
      photoURL
      phoneNumber
      role
    }
  }
`
const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      id
      displayName
      email
      phoneNumber
      role
    }
  }
`
const UPDATE_USER = gql`
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      id
      displayName
      email
      phoneNumber
      role
    }
  }
`
const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id) {
      id
      displayName
      email
      phoneNumber
      role
    }
  }
`

const UsersPage = () => {
  const [createUser] = useMutation(CREATE_USER)
  const [updateUser] = useMutation(UPDATE_USER)
  const [deleteUser] = useMutation(DELETE_USER)
  const { data, loading, error, fetchMore } = useQuery(USERS, {
    variables: {
      page: 0,
      size: 20,
      sort: { displayName: "ASC" },
    },
  })

  const handleCreate = async (input) => {
    const loadingId = toast.loading("Saving...")
    try {
      const user = await createUser({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("User created!")
      return user
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleUpdate = async (input) => {
    const loadingId = toast.loading("Saving...")
    try {
      const user = await updateUser({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("User updated!")
      return user
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleDelete = async (id) => {
    const loadingId = toast.loading("Saving...")
    try {
      const user = await deleteUser({ variables: { id } })
      toast.dismiss(loadingId)
      toast.success("User deleted!")
      return user
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleSearch = useCallback(
    (search) => {
      if (!fetchMore) return

      if (search?.trim() === "") {
        fetchMore({
          updateQuery: (
            previousResult,
            { fetchMoreResult, queryVariables },
          ) => {
            return {
              users: [...fetchMoreResult.users],
            }
          },
        })

        return
      }

      fetchMore({
        variables: { filter: { search } },
        updateQuery: (previousResult, { fetchMoreResult, queryVariables }) => {
          return {
            users: [
              // ...previousResult.matches,
              ...fetchMoreResult.users,
            ],
          }
        },
      })
    },
    [fetchMore],
  )

  const handleLoadMore = useCallback(
    (page) => {
      if (page === 0) return

      fetchMore({
        variables: { page },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult
          return Object.assign({}, previousResult, {
            users: [...previousResult.users, ...fetchMoreResult.users],
          })
        },
      })
    },
    [fetchMore],
  )

  const exportUsers = async () => {
    if (window.confirm(`Are you sure you want to export all users?`)) {
      try {
        await toast.promise(
          fetch(`${uri}/reporting/user/xlsx/`)
            .then((_) => _.blob())
            .then((blob) => {
              const file = window.URL.createObjectURL(blob)
              window.location.assign(file)
            }),
          {
            loading: "Creating export...",
            success: <b>Export created!</b>,
            error: <b>Could not save.</b>,
          },
        )
      } catch (error) {}
    }
  }

  const [selectedUser, setSelectedUser] = useState(null)
  const [isModalOpen, setModalOpen] = useState(false)

  const openModal = useCallback(async () => {
    setModalOpen(true)
  }, [setModalOpen])

  const closeModal = useCallback(async () => {
    setModalOpen(false)
    setSelectedUser(null)
  }, [setModalOpen])

  const handleSelect = (user) => {
    setSelectedUser(user)
    openModal()
  }

  const handleSubmit = async (data) => {
    if (data.id) {
      return await handleUpdate(data)
    } else {
      return await handleCreate(data)
    }
  }

  return (
    <Layout title="Users | GTR" isLoading={loading}>
      <BoxHeader
        title="Users"
        onExport={exportUsers}
        onSearch={handleSearch}
        onClick={() => openModal()}
      />

      <UserTable
        data={data?.users}
        onLoadMore={handleLoadMore}
        onSelect={handleSelect}
      />

      <UserModal
        data={selectedUser}
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />
    </Layout>
  )
}

export default UsersPage

const BoxHeader = ({ onSearch, onExport, onClick }) => {
  const [query, setQuery] = useState("")

  useEffect(() => {
    onSearch && onSearch(query)
  }, [query])

  return (
    <header className="bg-gray-800 text-gray-100 pb-24">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-12 gap-4">
        <div className="col-span-3 flex items-center">
          <h1 className="font-black text-3xl">Users</h1>
        </div>

        <div className="col-span-6 relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 fill-current text-gray-600"
            >
              <path d="M16.32 14.9l1.1 1.1c.4-.02.83.13 1.14.44l3 3a1.5 1.5 0 0 1-2.12 2.12l-3-3a1.5 1.5 0 0 1-.44-1.14l-1.1-1.1a8 8 0 1 1 1.41-1.41l.01-.01zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z" />
            </svg>
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="block w-full border border-transparent bg-gray-900 focus:outline-none focus:bg-white focus:border-gray-300 text-gray-900 rounded-lg pl-12 pr-4 py-2"
          />
        </div>

        <div className="col-span-3 flex space-x-4 justify-end items-center pl-8">
          <button
            onClick={onExport}
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </button>
          <button
            onClick={onClick}
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            User
          </button>
        </div>
      </div>
    </header>
  )
}

const UserTable = ({ data, onSelect, onLoadMore }) => {
  const header = ["Name", "Email", "Role", "Phone"]

  const hasMore = true
  const [page, loaderRef] = useInfiniteScroll({ hasMore })

  useEffect(() => {
    onLoadMore && onLoadMore(page)
  }, [page])

  return (
    <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
      <div className="relative -mt-16 z-30">
        <div className="bg-white rounded-lg shadow-lg">
          <div className="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
            <div style={{ maxHeight: "70vh", overflowY: "scroll" }}>
              <table className="min-w-full table-auto">
                <thead>
                  <tr>
                    {header.map((item, index) => (
                      <th
                        key={index}
                        className="px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                        style={{ position: "sticky", top: "0" }}
                      >
                        {item}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="table-body">
                  {data?.map((user, index) => (
                    <tr
                      key={index}
                      className="table-row"
                      onClick={() => onSelect(user)}
                    >
                      <td className="table-cell w-4/12">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <Avatar
                              name={user.displayName}
                              src={user.photoURL}
                              size="40"
                              round={true}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm leading-5 font-medium text-gray-900">
                              {user.displayName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell w-4/12">
                        <div className="text-sm leading-5 text-gray-900">
                          {user.email}
                        </div>
                      </td>
                      <td className="table-cell w-2/12">
                        <div className="text-sm leading-5 text-gray-900">
                          {user.role}
                        </div>
                      </td>
                      <td className="table-cell w-2/12">
                        <div className="text-sm leading-5 text-gray-900">
                          {user.phoneNumber}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {hasMore && (
                <div ref={loaderRef} className="text-center bg-gray-100">
                  Loading…
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}
